import { apiGet, apiSend } from '../../session/client';
import {
  type CourierMe,
  type CourierVerification,
  type DeliveryArea,
  type PlatformVehicle,
} from './courier-model';

/**
 * The platform routes the courier page uses. Every one already existed for the
 * JT Delivery app (courier onboarding, 2026-09-23); the website adds none.
 *
 * - `GET driver/me` — whether this person has applied, and where it stands.
 * - `GET delivery/areas?postcode=` — towns with pharmacies, nearest first (public).
 * - `POST documents/uploads` + `POST documents/:id/complete` — the ID and licence photos.
 * - `GET/POST person/me/contacts` — the mobile number, kept on the person.
 * - `POST driver/apply` — the application itself.
 */

export const getCourierMe = (): Promise<CourierMe> => apiGet<CourierMe>('/driver/me');

export const getDeliveryAreas = (postcode: string): Promise<readonly DeliveryArea[]> =>
  apiGet<readonly DeliveryArea[]>(`/delivery/areas?postcode=${encodeURIComponent(postcode)}`);

export interface ApplyBody {
  readonly displayName: string;
  readonly vehicle: PlatformVehicle;
  readonly idDocumentId: string;
  readonly licenceDocumentId?: string;
  readonly registrationNumber?: string;
  readonly storefront?: string;
}

export const applyAsCourier = (
  body: ApplyBody,
): Promise<{ id: string; verification: CourierVerification | null }> =>
  apiSend('POST', '/driver/apply', body);

interface UploadedDocument {
  readonly id: string;
  readonly status: string;
}

/**
 * The two-step upload the JT Delivery app uses: begin (the purpose and the
 * name), then the bytes. Identity evidence, owned by the person applying —
 * `driver/apply` refuses a document that is not the caller's own.
 */
export async function uploadEvidence(
  ownerPersonId: string,
  filename: string,
  bytes: Uint8Array,
): Promise<string> {
  const begun = await apiSend<UploadedDocument>('POST', '/documents/uploads', {
    purpose: 'IDENTITY_EVIDENCE',
    filename: filename.slice(0, 255),
    ownerPersonId,
  });
  const done = await apiSend<UploadedDocument>(
    'POST',
    `/documents/${encodeURIComponent(begun.id)}/complete`,
    {
      dataBase64: toBase64(bytes),
    },
  );
  return done.id;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

interface ContactPoint {
  readonly channel: string;
  readonly value: string;
}

/**
 * Keeps the mobile number on the person, unless it is there already. The
 * application itself has no phone field, so this is where the frame's
 * "Mobile number" lives.
 */
export async function keepMobile(value: string): Promise<void> {
  const existing = await apiGet<{ contacts: readonly ContactPoint[] }>('/person/me/contacts');
  if (existing.contacts.some((point) => point.channel === 'sms' && point.value === value)) return;
  await apiSend('POST', '/person/me/contacts', { channel: 'sms', value });
}
